import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Undo2, Loader2 } from "lucide-react";

export default function AdminFinance() {
  const [tx, setTx] = useState([]);
  const [stats, setStats] = useState(null);
  const [refundTx, setRefundTx] = useState(null);
  const [reason, setReason] = useState("Tvist, refunderet via admin");
  const [refunding, setRefunding] = useState(false);

  const refresh = () => {
    api.get("/admin/transactions").then(r => setTx(r.data));
    api.get("/admin/stats").then(r => setStats(r.data));
  };
  useEffect(() => { refresh(); }, []);

  const doRefund = async () => {
    setRefunding(true);
    try {
      await api.post("/refund", { transaction_id: refundTx.id, reason });
      toast.success("Refundering oprettet");
      setRefundTx(null);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Refund fejlede");
    } finally { setRefunding(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="text-3xl font-bold mb-6">Finansoversigt</h1>

      {stats && (
        <div className="grid sm:grid-cols-3 gap-4 mb-6">
          <Card className="p-5"><p className="text-xs text-muted-foreground">Omsætning</p><p className="text-2xl font-bold">{stats.total_revenue.toLocaleString("da-DK")} kr.</p></Card>
          <Card className="p-5"><p className="text-xs text-muted-foreground">Kommission (20%)</p><p className="text-2xl font-bold text-primary">{stats.total_commission.toLocaleString("da-DK")} kr.</p></Card>
          <Card className="p-5"><p className="text-xs text-muted-foreground">Transaktioner</p><p className="text-2xl font-bold">{stats.transaction_count}</p></Card>
        </div>
      )}

      <Card>
        <CardHeader><CardTitle>Alle transaktioner</CardTitle></CardHeader>
        <CardContent>
          {tx.length === 0 ? <p className="text-sm text-muted-foreground text-center py-6">Ingen transaktioner endnu</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-foreground border-b border-border">
                  <tr>
                    <th className="pb-2 pr-3">Dato</th><th className="pb-2 pr-3">Job</th>
                    <th className="pb-2 pr-3">Kunde</th><th className="pb-2 pr-3">Chauffør</th>
                    <th className="pb-2 pr-3 text-right">Total</th><th className="pb-2 pr-3 text-right">Chauffør</th>
                    <th className="pb-2 pr-3 text-right">Kommission</th><th className="pb-2 pr-3">Status</th>
                    <th className="pb-2 pr-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {tx.map(t => (
                    <tr key={t.id} className="border-b border-border last:border-0">
                      <td className="py-2 pr-3 text-xs">{new Date(t.created_at).toLocaleDateString("da-DK")}</td>
                      <td className="py-2 pr-3 text-xs">{t.job_id.slice(0,8)}</td>
                      <td className="py-2 pr-3 text-xs">{t.payer_email}</td>
                      <td className="py-2 pr-3 text-xs">{t.driver_email}</td>
                      <td className="py-2 pr-3 text-right font-semibold">{t.full_amount} kr.</td>
                      <td className="py-2 pr-3 text-right text-emerald-700">{t.driver_amount} kr.</td>
                      <td className="py-2 pr-3 text-right text-primary font-semibold">{t.grabber_commission} kr.</td>
                      <td className="py-2 pr-3"><Badge variant="outline" className="text-xs">{t.status}</Badge></td>
                      <td className="py-2 pr-3">
                        {t.status === "completed" && (
                          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => setRefundTx(t)} data-testid={`refund-btn-${t.id}`}>
                            <Undo2 className="w-3 h-3" /> Refundér
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!refundTx} onOpenChange={(o) => !o && setRefundTx(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Bekræft refundering</DialogTitle></DialogHeader>
          {refundTx && (
            <div className="space-y-3">
              <p className="text-sm">Refunder <span className="font-bold">{refundTx.full_amount} DKK</span> til <span className="font-medium">{refundTx.payer_email}</span> via Stripe.</p>
              <p className="text-xs text-muted-foreground">Job markeres som annulleret. Begge parter får besked.</p>
              <div className="space-y-1.5">
                <Label>Årsag</Label>
                <Input value={reason} onChange={(e) => setReason(e.target.value)} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundTx(null)}>Annuller</Button>
            <Button onClick={doRefund} disabled={refunding} data-testid="confirm-refund-btn">
              {refunding && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Bekræft refundering
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
